// components/ui/SlashCommandMenu.tsx
import React from 'react';

export interface SlashMenuCommand {
  name: string;
  hint: string;
  group?: string;
}

// ChatGPT's popover menu for '/' commands (owner, 2026-09-29: '/chatgptize this menu in all chat
// inputs'): a #353535 rounded panel, grouped under quiet sentence-case labels, one row per command
// with its name and a single grey line that ends in an ellipsis instead of scrolling sideways.
// One definition for every composer; it sits in the composer's flow above the text.
export function SlashCommandMenu<C extends SlashMenuCommand>({ commands, activeIndex, groupLabel, onPick, onHover }: {
  commands: C[];
  activeIndex: number;
  groupLabel: (group: C['group']) => string;
  onPick: (command: C) => void;
  onHover: (index: number) => void;
}) {
  if (commands.length === 0) return null;
  return (
    <div role="listbox" className="custom-scrollbar mb-1.5 max-h-[320px] overflow-y-auto rounded-[20px] border border-white/[0.06] bg-[#353535] p-1.5 shadow-[0_12px_32px_rgba(0,0,0,0.45)]">
      {commands.map((command, index) => (
        <React.Fragment key={command.name}>
          {index === 0 || commands[index - 1]?.group !== command.group ? (
            <div className="sticky top-[-6px] z-10 bg-[#353535] px-3 pb-1 pt-2 text-[#8E8E8E]" style={{ fontSize: 13 }}>
              {groupLabel(command.group)}
            </div>
          ) : null}
          <button
            type="button"
            role="option"
            aria-selected={index === activeIndex}
            ref={index === activeIndex ? (el) => el?.scrollIntoView({ block: 'nearest' }) : undefined}
            onMouseDown={(event) => { event.preventDefault(); onPick(command); }}
            onMouseEnter={() => onHover(index)}
            className={`flex w-full min-w-0 items-baseline gap-2.5 rounded-[12px] px-3 py-2 text-left transition-colors ${index === activeIndex ? 'bg-white/[0.08]' : 'hover:bg-white/[0.05]'}`}
          >
            <span className="shrink-0 text-[#ECECEC]" style={{ fontSize: 14 }}>/{command.name}</span>
            <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-[#AFAFAF]" style={{ fontSize: 13 }} title={command.hint}>{command.hint}</span>
          </button>
        </React.Fragment>
      ))}
    </div>
  );
}
